import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useOutletContext, useParams } from 'react-router';
import { Loader2 } from 'lucide-react';
import { emit } from '@/lib/analytics';
import { AppShell } from '@/components/app-shell';
import { AppHeader, defaultHeaderActions } from '@/components/app-header';
import { OrgSidebar } from '@/components/org-sidebar';
import { ConnectChannelModal } from '@/components/connect-channel-modal';
import { useFetch } from '@/hooks/use-fetch';
import { isValidPublicId } from '@/lib/public-id';
import { initChatwoot, identifyChatwootUser, resetChatwootIdentity } from '@/lib/chatwoot';
import { useProfile } from '@/contexts/profile-context';
import { useSession } from '@/contexts/session-context';
import {
  clearOrgNavigationPending,
  getPendingOrgNavigation,
  msUntilPendingOrgNavigationExpires,
} from '@/lib/pending-org-navigation';

/** GET /organizations/:orgId response (roadmap contract 3). */
export interface OrgInfo {
  publicId: string;
  name: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  businessCategory: string | null;
  businessNiche: string | null;
  primaryUseCase: string | null;
}

export interface OrgOutletContext {
  org: OrgInfo | null;
  refreshOrg: () => Promise<void>;
  /** Opens the org-scoped Connect Channel modal (target picked in the modal).
   *  Pass `{ customerOnly: true }` to lock the picker to customers. */
  openConnectChannel: (opts?: { customerOnly?: boolean }) => void;
}

/** Typed accessor for org pages rendered inside OrgLayout's <Outlet />. */
export function useOrg(): OrgOutletContext {
  return useOutletContext<OrgOutletContext>();
}

/**
 * R3 OrgLayout -- chrome for the Organization view (/org/:orgId/*).
 *
 * Access validation: GET /organizations/:orgId (roadmap contract 3, gated to
 * org members server-side). 200 renders the view; 403/404 silently redirect
 * to `/` (Phase 117/118 stale-id convention, no 403/404 page).
 *
 * Deliberately NOT validated against useWorkspace().workspaces: org access is
 * an org-membership question, not a workspace-list question. The R2
 * GET /workspaces union does include org workspaces for org admins, but an
 * org with zero workspaces still yields an empty list for its rightful admin
 * (spec Decision 4: admins have implicit access, no roster rows required), so
 * the list cannot gate this view. WorkspaceProvider stays mounted above this
 * layout purely as a convenience for the org pages (refreshWorkspaces after
 * create, usage data inside BillingSection).
 */
export function OrgLayout() {
  const { orgId } = useParams<{ orgId: string }>();
  const fetchWithAuth = useFetch();
  const location = useLocation();
  const { profile } = useProfile();
  const { organizationId, switchOrganization } = useSession();

  // Support widget (Oscar) — same hookup as DashboardLayout so org pages
  // loaded directly (hard refresh) also get the widget.
  useEffect(() => {
    initChatwoot();
    if (profile) identifyChatwootUser(profile, fetchWithAuth);
    else resetChatwootIdentity();
  }, [profile, fetchWithAuth]);
  // Connect-modal picker follows the page it was opened from: customer-facing
  // pages (/channels = customer channels, /customers) default the target
  // picker to Customers; team pages (/workspaces*) and the rest to Workspaces.
  const onCustomerPage =
    /\/(channels|customers)\/?$/.test(location.pathname) &&
    !location.pathname.includes('/workspaces/');
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [access, setAccess] = useState<'loading' | 'granted' | 'denied' | 'out-of-scope'>(
    'loading',
  );
  // The org whose re-scope the server refused — the only real denial there is
  // for an out-of-scope org, and it must outlive a refetch (AIT-539).
  const [rescopeRefusedFor, setRescopeRefusedFor] = useState<string | null>(null);
  // Bumped when a pending navigation's lease can have expired, purely to
  // re-run the reconcile effect below (AIT-539).
  const [leaseRecheck, setLeaseRecheck] = useState(0);
  const [connectOpen, setConnectOpen] = useState(false);
  const [connectCustomerOnly, setConnectCustomerOnly] = useState(false);
  const handleConnectClick = (opts?: { customerOnly?: boolean }) => {
    emit('connect_channel_modal_opened', { scope: 'org' });
    setConnectCustomerOnly(opts?.customerOnly ?? false);
    setConnectOpen(true);
  };

  const refreshOrg = useCallback(async () => {
    if (!orgId || !isValidPublicId(orgId, 'org')) return;
    try {
      const res = await fetchWithAuth(`/organizations/${orgId}`);
      if (res.ok) {
        setOrg((await res.json()) as OrgInfo);
        setAccess('granted');
      } else if (res.status === 403 || res.status === 404) {
        // AIT-539: a 403 here answers "is this org in scope for the session
        // cookie you just sent", NOT "may this user see this org". The session
        // org flips out from under this view on every switch/create (both
        // re-seal the cookie, then hard-navigate), so for ~half a second the
        // URL still carries the PREVIOUS :orgId and its refetch 403s. Bouncing
        // on that dropped the user on `/` instead of the org they picked.
        // Hand a mismatch to the reconcile effect below; only a 403 on the org
        // the session IS scoped to is a real denial.
        // A re-scope already refused for THIS org is terminal: without that
        // memory `refreshOrg` re-fires (its identity changes with the session),
        // flips the state back to out-of-scope, and the one-attempt guard below
        // leaves the view spinning on a loop it can never leave.
        setAccess(
          orgId && orgId !== organizationId && rescopeRefusedFor !== orgId
            ? 'out-of-scope'
            : 'denied',
        );
      } else {
        // Non-403/404 unexpected status (e.g. 429, 503). useFetch's 5xx
        // interceptor replaces the whole tree with the global ErrorPage for
        // true server errors, so this branch only governs other unexpected
        // non-5xx statuses. Render the shell so pages can surface their own
        // error context.
        setAccess('granted');
      }
    } catch (err) {
      // Network errors are surfaced by useFetch's error page for GETs, but a
      // malformed success body (e.g. HTML served where JSON was expected, as
      // happened when /organizations was missing from the Vite proxy) lands
      // here. Render the shell rather than stranding the loading spinner
      // forever: the broken state stays visible and debuggable.
      console.error('OrgLayout: failed to load the organization', err);
      setAccess('granted');
    }
  }, [orgId, fetchWithAuth, organizationId, rescopeRefusedFor]);

  // AIT-539 — arriving at the marked org retires its marker, on ANY outcome.
  // The marker must not outlive the navigation it describes: left behind, it
  // makes the next out-of-scope org in this tab look like an in-flight
  // navigation, and this view waits for a page that is never coming.
  useEffect(() => {
    if (orgId && getPendingOrgNavigation() === orgId) clearOrgNavigationPending();
  }, [orgId]);

  // AIT-539 — reconcile an out-of-scope org instead of bouncing.
  //
  // A 403 here means the session is not scoped to this :orgId. That is only a
  // denial if nothing is going to change the scope:
  //
  //   - THIS tab is mid-switch (`switchOrganization` re-seals the cookie, then
  //     the caller hard-navigates). The navigation owns what happens next, so
  //     wait for it. Per-tab marker, so a switch made in ANOTHER tab — which
  //     this tab picks up through the session focus refresh — does not qualify
  //     and falls through to the re-scope below.
  //   - Otherwise (cold entry from a bookmark or shared link, or a foreign
  //     tab's switch) nothing is coming, so re-scope into the org the URL asked
  //     for. Membership is the SERVER's call: POST /auth/switch-organization
  //     rejects an org the user does not belong to. Inferring it client-side
  //     from the workspace union would deny a rightful admin of an org that has
  //     no workspaces yet (see this component's JSDoc).
  //
  // One attempt per :orgId; a rejected re-scope is the genuine dead end.
  const reconciledRef = useRef<string | null>(null);
  useEffect(() => {
    if (access !== 'out-of-scope' || !orgId) return;
    const pendingTarget = getPendingOrgNavigation();
    if (pendingTarget !== null && pendingTarget !== orgId) {
      // A navigation that never lands (cancelled, or a restored page) changes
      // none of this effect's dependencies, so the lease would expire unseen and
      // leave the view waiting forever. Wake up once at the boundary and
      // re-evaluate; by then the marker is gone and the branch below re-scopes.
      const handle = setTimeout(
        () => setLeaseRecheck((n) => n + 1),
        msUntilPendingOrgNavigationExpires(),
      );
      return () => clearTimeout(handle);
    }
    if (reconciledRef.current === orgId) return;
    reconciledRef.current = orgId;
    void switchOrganization(orgId)
      .then(() => {
        // Release the guard: it exists to stop a re-entrant attempt while one
        // is in flight, NOT to ban this org forever. Another tab can flip the
        // shared cookie again later, and the focus refresh brings that change
        // here — a second re-scope is then exactly what is needed.
        reconciledRef.current = null;
        setAccess('loading');
      })
      // Left set on failure: a refused org must not be retried in a loop, and
      // `rescopeRefusedFor` makes the next 403 terminal.
      .catch(() => setRescopeRefusedFor(orgId));
  }, [access, orgId, switchOrganization, leaseRecheck]);

  // GET /organizations/:orgId is an external bootstrap call whose result
  // populates state once on mount (and on orgId change). Calling refreshOrg()
  // inside an async IIFE keeps setState off the synchronous effect body so
  // the set-state-in-effect analyzer doesn't flag it (same pattern as
  // session-context.tsx).
  useEffect(() => {
    void (async () => {
      await refreshOrg();
    })();
  }, [refreshOrg]);

  if (!orgId || !isValidPublicId(orgId, 'org')) {
    return <Navigate to="/" replace />;
  }

  if (access === 'denied') {
    return <Navigate to="/" replace />;
  }

  if (access === 'loading' || access === 'out-of-scope') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <AppShell
      sidebar={
        <OrgSidebar
          orgName={org?.name ?? null}
          onConnectClick={handleConnectClick}
        />
      }
      header={<AppHeader actions={defaultHeaderActions()} />}
      afterChrome={
        <ConnectChannelModal
          open={connectOpen}
          onOpenChange={setConnectOpen}
          orgId={orgId}
          customerOnly={connectCustomerOnly}
          defaultKind={onCustomerPage ? 'customer' : 'team'}
          fetchWithAuth={fetchWithAuth}
        />
      }
    >
      <Outlet
        context={
          { org, refreshOrg, openConnectChannel: handleConnectClick } satisfies OrgOutletContext
        }
      />
    </AppShell>
  );
}
